import { forwardRef, HTMLAttributes, ReactNode, Ref, useCallback, useEffect, useState } from 'react';

import { IComponent, IconName } from '../../models/components.models';
import { FluentClickHandler } from '../../models/types';
import { useClassNames } from '../../utils/hooks';
import { Button } from '../Button/Button';
import { buttonStylesPrimary, buttonStylesStealth } from '../Button/Button.styles';
import { dashboardStylesDefault, IDashboardClassNames, IDashboardStyles } from './Dashboard.styles';

export enum DashboardRowLayout {
  one = 'one',
  oneOne = 'oneOne',
  oneOneOne = 'oneOneOne',
  oneTwo = 'oneTwo',
  twoOne = 'twoOne',
  oneThree = 'oneThree',
  threeOne = 'threeOne',
}

export interface DashboardRow {
  id?: string;
  layout?: DashboardRowLayout;
  widgetIds?: (string | DashboardRow)[]; //TODO: change name to widgetIdOrRows
  height?: number;
  vertical?: boolean;
}

type CellRenderer = (cellKey: string, windowWidth: number, rowIndex?: number, cellIndex?: number) => ReactNode;

export interface IDashboardProps extends IComponent<IDashboardStyles>, HTMLAttributes<HTMLDivElement> {
  title?: string;
  rows?: DashboardRow[];
  rowHeight?: number;
  showControls?: boolean;
  cellRenderer: CellRenderer;
  onClickConfigure?: FluentClickHandler;
  onClickAddWidget?: FluentClickHandler;
}

const renderWidgets = (
  row: DashboardRow | undefined,
  rowIndex: number,
  classNames: IDashboardClassNames,
  cellRenderer: CellRenderer,
  windowWidth: number
) => {
  return row && row.widgetIds
    ? row.widgetIds.map((idOrRow, cellIndex) => {
        const row: DashboardRow | undefined = typeof idOrRow !== 'string' ? idOrRow : undefined;
        const id = typeof idOrRow !== 'string' ? row?.id || '' : idOrRow;
        return (
          <div
            key={`${rowIndex}-${cellIndex}`}
            style={row && row.height ? { height: row.height, flex: 'none' } : undefined}
            className={
              row && row.widgetIds
                ? `${classNames.container}${row.vertical ? ' vertical' : ''} ${row.layout}`
                : `${classNames.widget}`
            }
          >
            {!row || !row.widgetIds
              ? cellRenderer(id, windowWidth, rowIndex, cellIndex)
              : renderWidgets(row, cellIndex, classNames, cellRenderer, windowWidth)}
          </div>
        );
      })
    : null;
};

export const Dashboard = forwardRef((props: IDashboardProps, ref: Ref<HTMLDivElement>) => {
  const {
    styles,
    className,
    title,
    rows,
    rowHeight = 392,
    showControls,
    cellRenderer,
    onClickAddWidget,
    onClickConfigure,
    ...elementProps
  } = props;
  const [windowWidth, setWindowWidth] = useState<number>(0);
  const classNames = useClassNames<Partial<IDashboardStyles>, IDashboardClassNames>(
    'Dashboard',
    dashboardStylesDefault,
    styles,
    className,
    !title && !showControls && 'no-header'
  );
  // when cells need to handle window resize event, they can use `windowWidth` value to check whether the width of window is changed or not.
  const updateSize = useCallback(() => {
    // `window` can be gone during an async-triggered re-render after unmount (e.g. jsdom teardown in tests);
    // bare access would throw a ReferenceError, so no-op when it is absent.
    if (typeof window === 'undefined') return;
    if (windowWidth !== window.innerHeight) {
      setWindowWidth(window.innerWidth);
    }
  }, [windowWidth]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    updateSize();
    window.removeEventListener('resize', updateSize);
    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, [updateSize]);
  return (
    <div {...elementProps} ref={ref} className={classNames.root} data-test={props['data-test'] || 'dashboard'}>
      {(title || showControls) && (
        <div className={classNames.header}>
          {title && <div className={classNames.title}>{title}</div>}
          {showControls && (
            <div className={classNames.controls}>
              <Button
                styles={buttonStylesStealth}
                iconName={IconName.Settings}
                text="Configure"
                onClick={onClickConfigure}
              />
              <Button styles={buttonStylesPrimary} text="Add Widget" onClick={onClickAddWidget} />
            </div>
          )}
        </div>
      )}
      <div className={classNames.body}>
        {renderWidgets(
          rows ? { widgetIds: rows.map((row) => ({ ...row, height: row.height || rowHeight })) } : undefined,
          0,
          classNames,
          cellRenderer,
          windowWidth
        )}
      </div>
    </div>
  );
});
